/**
 * Voice choices shared by Voice Mode, Read Aloud, and settings previews.
 * `RESPONSE_AUDIO_VOICES` lists accepted IDs, including internal-only ones the
 * picker offers only while internal-only UI shows. Invalid saved IDs pass
 * through `normalizeResponseAudioVoiceId` and become the default.
 */
export const RESPONSE_AUDIO_VOICES = [
  { id: 'elevenlabs:XrExE9yKIg1WjnnlVkGX', name: 'Matilda (ElevenLabs)' },
  { id: 'elevenlabs:hpp4J3VqNfWAUOO0d1Us', name: 'Bella (ElevenLabs)' },
  { id: 'elevenlabs:QtY3JBOUKEB5xzrRfOKc', name: 'Maisie (ElevenLabs)' },
  { id: 'elevenlabs:jSuBIjxMKhqIfb0wCK1F', name: 'Baxter (ElevenLabs)' },
  {
    id: 'elevenlabs:G17SuINrv2H9FC6nvetn',
    name: 'Christopher (ElevenLabs)',
  },
  { id: 'elevenlabs:l7kNoIfnJKPg7779LI2t', name: 'Eddie (ElevenLabs)' },
  { id: 'elevenlabs:fCxG8OHm4STbIsWe4aT9', name: 'Harrison (ElevenLabs)' },
  { id: 'elevenlabs:zGjIP4SZlMnY9m93k97r', name: 'Hope (ElevenLabs)' },
  {
    id: 'elevenlabs-v4:QtY3JBOUKEB5xzrRfOKc',
    name: 'Maisie (ElevenLabs v4)',
    internalOnly: true,
  },
  {
    id: 'elevenlabs-v4:jSuBIjxMKhqIfb0wCK1F',
    name: 'Baxter (ElevenLabs v4)',
    internalOnly: true,
  },
  {
    id: 'elevenlabs-v4-turbo:QtY3JBOUKEB5xzrRfOKc',
    name: 'Maisie (ElevenLabs v4 Turbo)',
    internalOnly: true,
  },
  {
    id: 'elevenlabs-v4-turbo:jSuBIjxMKhqIfb0wCK1F',
    name: 'Baxter (ElevenLabs v4 Turbo)',
    internalOnly: true,
  },
  { id: 'echo', name: 'Echo (OpenAI)' },
  { id: 'fable', name: 'Fable (OpenAI)' },
  { id: 'marin', name: 'Marin (OpenAI)' },
  { id: 'shimmer', name: 'Shimmer (OpenAI)' },
  { id: 'deepgram:aura-asteria-en', name: 'Asteria (Deepgram)' },
  { id: 'deepgram:aura-helios-en', name: 'Helios (Deepgram)' },
  { id: 'deepgram:aura-hera-en', name: 'Hera (Deepgram)' },
  { id: 'deepgram:aura-luna-en', name: 'Luna (Deepgram)' },
  {
    id: 'cartesia:db6b0ed5-d5d3-463d-ae85-518a07d3c2b4',
    name: 'Skylar (Cartesia)',
    internalOnly: true,
  },
  {
    id: 'cartesia:a5136bf9-224c-4d76-b823-52bd5efcffcc',
    name: 'Jameson (Cartesia)',
    internalOnly: true,
  },
] as const;

/** Voices the picker offers; internal-only ones only while internal-only UI shows */
export function getAvailableResponseAudioVoices(isInternalUser: boolean) {
  return RESPONSE_AUDIO_VOICES.filter(
    (voice) => isInternalUser || !('internalOnly' in voice)
  );
}

export const DEFAULT_RESPONSE_AUDIO_VOICE_ID =
  'elevenlabs:QtY3JBOUKEB5xzrRfOKc';

/**
 * Preview lines for the voice picker; every preview plays one at random.
 * Each is 3-6 spoken seconds with a pause or a question in it, so a voice
 * shows its pacing and pitch range, not just its timbre.
 */
export const VOICE_SAMPLE_TEXTS = [
  'It was the best of times, it was the worst of times, it was the age of wisdom, it was the age of foolishness.',
  'Call me Ishmael. Some years ago — never mind how long precisely — I thought I would sail about a little.',
  'It is a truth universally acknowledged, that a single man in possession of a good fortune, must be in want of a wife.',
  'All happy families are alike; each unhappy family is unhappy in its own way.',
  "Here's what you've got on your plate: two meetings this afternoon, one draft waiting on you, and nothing urgent before noon.",
  "Hi — this is how I'll sound when I read your replies. Comfortable enough to listen to all day?",
  'Morning! Coffee first, or should I just tell you what happened while you were gone?',
  'Testing, testing — one, two, three. How does this voice sound to you?',
  'Some days ask for speed. This one, apparently, asks for patience — and a second cup of tea.',
  'Quick question: if you could clear one thing off your plate today, what would it be?',
  'You know that feeling when everything finally goes quiet? This is what it sounds like.',
] as const;

/** Pick the line one voice preview will speak */
export function pickVoiceSampleText(): string {
  return VOICE_SAMPLE_TEXTS[
    Math.floor(Math.random() * VOICE_SAMPLE_TEXTS.length)
  ]!;
}

/**
 * ID reserved for the settings-page preview. It lets the page stop its own
 * preview when unmounting without stopping a message being read elsewhere.
 */
export const VOICE_SAMPLE_READ_ALOUD_ID = 'voice-sample:settings';

export function normalizeResponseAudioVoiceId(raw: unknown): string {
  return typeof raw === 'string' &&
    RESPONSE_AUDIO_VOICES.some((voice) => voice.id === raw)
    ? raw
    : DEFAULT_RESPONSE_AUDIO_VOICE_ID;
}

export function getResponseAudioVoiceName(voiceId: string): string {
  return (
    RESPONSE_AUDIO_VOICES.find((voice) => voice.id === voiceId)?.name ??
    'Custom voice'
  );
}
